<template>
    <main
        class="min-w-0 flex-1 border-t border-taupe-200 lg:flex overflow-y-auto bg-taupe-50"
    >
        <!-- Primary column -->
        <section
            aria-labelledby="primary-heading"
            class="flex h-full min-w-0 flex-1 flex-col lg:order-last pt-6 sm:pt-8 lg:pt-10 xl:max-w-7xl xl:mx-auto w-full"
        >
            <h1 id="primary-heading" class="sr-only">Admin: Users</h1>
            <div class="px-4 sm:px-6 lg:px-8 pb-12 space-y-8">
                <!-- Header with tabs and action button -->
                <div class="mb-6">
                    <admin-header-tabs current="users" />
                </div>

                <!-- Page Title and Action -->
                <div
                    class="flex items-center justify-between mb-6 min-h-[2.5rem]"
                >
                    <h2 class="text-2xl font-medium text-taupe-900">Users</h2>
                    <div class="flex-shrink-0">
                        <zen-button
                            v-if="!loading && can(PERMISSIONS.USER_CREATE)"
                            variant="primary"
                            size="medium"
                            @click="openCreateModal()"
                        >
                            <template #icon>
                                <svg
                                    class="h-5 w-5"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                    aria-hidden="true"
                                >
                                    <path
                                        d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z"
                                    />
                                </svg>
                            </template>
                            Invite User
                        </zen-button>
                    </div>
                </div>

                <zen-loading-indicator v-if="loading" />

                <!-- Users Table -->
                <zen-grid v-if="!loading">
                    <zen-grid-table>
                        <zen-grid-header>
                            <zen-grid-header-row>
                                <zen-grid-header-cell
                                    :hidable="false"
                                    title="User"
                                />
                                <zen-grid-header-cell title="Grants" />
                                <zen-grid-header-cell title="Teams" />
                                <zen-grid-header-cell title="Last seen" />
                                <zen-grid-header-cell
                                    alignment="right"
                                    title="Actions"
                                />
                            </zen-grid-header-row>
                        </zen-grid-header>
                        <zen-grid-body>
                            <zen-grid-body-row
                                v-for="result in results"
                                :key="result.ulid"
                            >
                                <zen-grid-body-cell
                                    :hidable="false"
                                    :primary="true"
                                >
                                    <div class="flex flex-col">
                                        <div class="font-medium text-taupe-900">
                                            {{ result.first_name }}
                                            {{ result.last_name }}
                                        </div>
                                        <div class="text-sm text-taupe-500">
                                            {{ result.email }}
                                        </div>
                                    </div>
                                    <zen-grid-mobile-detail>
                                        <zen-grid-mobile-detail-item
                                            label="Grants"
                                        >
                                            <div
                                                v-if="
                                                    (
                                                        userGrants[
                                                            result.ulid
                                                        ] ?? []
                                                    ).length
                                                "
                                                class="flex flex-col gap-2"
                                            >
                                                <div
                                                    v-for="grant in userGrants[
                                                        result.ulid
                                                    ] ?? []"
                                                    :key="grant.ulid"
                                                    class="inline-flex items-center gap-1.5 w-fit whitespace-nowrap"
                                                >
                                                    <zen-inline-pill
                                                        :value="grant.role"
                                                    />
                                                    <span
                                                        class="text-xs text-taupe-600"
                                                        >@
                                                        {{
                                                            scopeLabel(grant)
                                                        }}</span
                                                    >
                                                </div>
                                            </div>
                                            <span v-else class="text-taupe-400"
                                                >—</span
                                            >
                                        </zen-grid-mobile-detail-item>
                                        <zen-grid-mobile-detail-item
                                            label="Teams"
                                        >
                                            <div
                                                v-if="
                                                    (
                                                        userTeamMemberships[
                                                            result.ulid
                                                        ] ?? []
                                                    ).length
                                                "
                                                class="flex flex-col gap-2"
                                            >
                                                <div
                                                    v-for="team in userTeamMemberships[
                                                        result.ulid
                                                    ] ?? []"
                                                    :key="team.ulid"
                                                    class="inline-flex items-center w-fit"
                                                >
                                                    <zen-inline-pill
                                                        :value="team.name"
                                                        colorClass="purple-600"
                                                    />
                                                </div>
                                            </div>
                                            <span v-else class="text-taupe-400"
                                                >—</span
                                            >
                                        </zen-grid-mobile-detail-item>
                                        <zen-grid-mobile-detail-item
                                            label="Last seen"
                                        >
                                            <zen-date-time
                                                human
                                                :value="result.last_seen_at"
                                                blank="Never"
                                            />
                                        </zen-grid-mobile-detail-item>
                                        <div class="flex gap-1 pt-1">
                                            <zen-table-icon
                                                v-if="
                                                    currentUserUlid !=
                                                        result.ulid &&
                                                    can(PERMISSIONS.USER_UPDATE)
                                                "
                                                @click="openUpdateModal(result)"
                                                :srText="
                                                    'Update ' +
                                                    result.first_name +
                                                    ' ' +
                                                    result.last_name
                                                "
                                                tooltipText="Update"
                                            >
                                                <zen-icon-update />
                                            </zen-table-icon>
                                            <zen-table-icon
                                                v-if="isAdmin"
                                                @click="openGrantsPanel(result)"
                                                :srText="
                                                    'Manage grants for ' +
                                                    result.first_name +
                                                    ' ' +
                                                    result.last_name
                                                "
                                                tooltipText="Manage Grants"
                                            >
                                                <svg
                                                    class="h-6 w-6"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path
                                                        d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z"
                                                    />
                                                </svg>
                                            </zen-table-icon>
                                            <zen-table-icon
                                                v-if="isAdmin"
                                                @click="openTeamsPanel(result)"
                                                :srText="
                                                    'Manage teams for ' +
                                                    result.first_name +
                                                    ' ' +
                                                    result.last_name
                                                "
                                                tooltipText="Manage Teams"
                                            >
                                                <svg
                                                    class="h-6 w-6"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                >
                                                    <path
                                                        d="M18 18.72a9.094 9.094 0 0 0 3.741-.479 3 3 0 0 0-4.682-2.72m.94 3.198.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0 1 12 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 0 1 6 18.719m12 0a5.971 5.971 0 0 0-.941-3.197m0 0A5.995 5.995 0 0 0 12 12.75a5.995 5.995 0 0 0-5.058 2.772m0 0a3 3 0 0 0-4.681 2.72 8.986 8.986 0 0 0 3.74.477m.94-3.197a5.971 5.971 0 0 0-.94 3.197M15 6.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Zm6 3a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Zm-13.5 0a2.25 2.25 0 1 1-4.5 0 2.25 2.25 0 0 1 4.5 0Z"
                                                    />
                                                </svg>
                                            </zen-table-icon>
                                            <zen-table-icon
                                                v-if="
                                                    currentUserUlid !=
                                                        result.ulid &&
                                                    result.verified
                                                "
                                                @click="
                                                    openResetModal(result.ulid)
                                                "
                                                :srText="
                                                    'Reset password for ' +
                                                    result.first_name +
                                                    ' ' +
                                                    result.last_name
                                                "
                                                tooltipText="Reset Password"
                                            >
                                                <zen-icon-email-share />
                                            </zen-table-icon>
                                            <zen-table-icon
                                                v-if="
                                                    currentUserUlid !=
                                                        result.ulid &&
                                                    !result.verified
                                                "
                                                @click="
                                                    openResendModal(result.ulid)
                                                "
                                                :srText="
                                                    'Resend welcome email to ' +
                                                    result.first_name +
                                                    ' ' +
                                                    result.last_name
                                                "
                                                tooltipText="Resend Welcome Email"
                                            >
                                                <zen-icon-email-share />
                                            </zen-table-icon>
                                            <zen-table-icon
                                                v-if="
                                                    currentUserUlid !=
                                                        result.ulid &&
                                                    can(PERMISSIONS.USER_DELETE)
                                                "
                                                @click="
                                                    openRemoveModal(result.ulid)
                                                "
                                                :srText="
                                                    'Remove ' +
                                                    result.first_name +
                                                    ' ' +
                                                    result.last_name
                                                "
                                                tooltipText="Remove"
                                            >
                                                <zen-icon-remove />
                                            </zen-table-icon>
                                        </div>
                                    </zen-grid-mobile-detail>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <div class="flex flex-col gap-2">
                                        <div
                                            v-for="grant in userGrants[
                                                result.ulid
                                            ] ?? []"
                                            :key="grant.ulid"
                                            class="inline-flex items-center gap-2 w-fit whitespace-nowrap"
                                        >
                                            <zen-inline-pill
                                                :value="grant.role"
                                            />
                                            <span class="text-sm text-taupe-600"
                                                >@ {{ scopeLabel(grant) }}</span
                                            >
                                        </div>
                                        <span
                                            v-if="
                                                !(userGrants[result.ulid] ?? [])
                                                    .length
                                            "
                                            class="text-sm text-taupe-400"
                                        >
                                            —
                                        </span>
                                    </div>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <div class="flex flex-col gap-2">
                                        <div
                                            v-for="team in userTeamMemberships[
                                                result.ulid
                                            ] ?? []"
                                            :key="team.ulid"
                                            class="inline-flex items-center w-fit"
                                        >
                                            <zen-inline-pill
                                                :value="team.name"
                                                colorClass="purple-600"
                                            />
                                        </div>
                                        <span
                                            v-if="
                                                !(
                                                    userTeamMemberships[
                                                        result.ulid
                                                    ] ?? []
                                                ).length
                                            "
                                            class="text-sm text-taupe-400"
                                        >
                                            —
                                        </span>
                                    </div>
                                </zen-grid-body-cell>
                                <zen-grid-body-cell>
                                    <zen-date-time
                                        human
                                        :value="result.last_seen_at"
                                        blank="Never"
                                    />
                                </zen-grid-body-cell>
                                <zen-grid-body-cell alignment="right">
                                    <div class="hidden lg:flex justify-end">
                                        <user-action-menu
                                            :user="result"
                                            :current-user-ulid="currentUserUlid"
                                            :is-admin="isAdmin"
                                            @update="openUpdateModal"
                                            @manage-grants="openGrantsPanel"
                                            @manage-teams="openTeamsPanel"
                                            @reset="openResetModal"
                                            @resend="openResendModal"
                                            @remove="openRemoveModal"
                                        />
                                    </div>
                                </zen-grid-body-cell>
                            </zen-grid-body-row>
                        </zen-grid-body>
                    </zen-grid-table>
                </zen-grid>
            </div>
        </section>

        <remove-user-modal
            :is-open="modals.remove.show"
            :processing="modals.remove.processing"
            @close="toggleRemoveModal"
            @confirm="confirmRemove"
        />

        <resend-welcome-email-modal
            :is-open="modals.resend.show"
            :processing="modals.resend.processing"
            @close="toggleResendModal"
            @confirm="confirmResend"
        />

        <reset-password-modal
            :is-open="modals.reset.show"
            :processing="modals.reset.processing"
            @close="toggleResetModal"
            @confirm="confirmReset"
        />

        <invite-user-slide-in
            :is-open="modals.create.show"
            v-model:data="modals.create.data"
            :error="modals.create.error"
            :can-submit="canSubmitCreate"
            :processing="modals.create.processing"
            @close="toggleCreateModal"
            @confirm="confirmCreate()"
        />

        <update-user-slide-in
            :is-open="modals.update.show"
            v-model:data="modals.update.data"
            :error="modals.update.error"
            :can-submit="canSubmitUpdate"
            :processing="modals.update.processing"
            @close="toggleUpdateModal"
            @confirm="confirmUpdate()"
        />

        <manage-user-grants-slide-in
            :is-open="grantsPanel.show"
            :user-name="fullName(grantsPanel.user)"
            :grants="grantsPanel.grants"
            :scope-label="scopeLabel"
            :is-admin="isAdmin"
            @close="closeGrantsPanel"
            @add-grant="openAddGrantModal"
            @revoke-grant="revokeGrantConfirm"
        />

        <add-user-grant-slide-in
            :is-open="grantModal.show"
            v-model:data="grantModal.data"
            :error="grantModal.error"
            :processing="grantModal.processing"
            :role-description="grantRoleDescription"
            :can-submit="canSubmitGrant"
            :user-name="fullName(grantsPanel.user)"
            :projects="projects"
            :selected-project-environments="selectedProjectEnvironments"
            @close="closeAddGrantModal"
            @confirm="submitGrant"
        />

        <revoke-user-grant-modal
            :is-open="revokeModal.show"
            :grant="revokeModal.grant"
            :user-name="fullName(grantsPanel.user)"
            :processing="revokeModal.processing"
            :scope-label="scopeLabel"
            @close="closeRevokeModal"
            @confirm="confirmRevoke"
        />

        <manage-user-teams-slide-in
            :is-open="teamsPanel.show"
            :user-name="fullName(teamsPanel.user)"
            :teams="teamsPanel.teams"
            v-model="teamsPanel.selectedTeamUlid"
            :search-available-teams="searchAvailableTeams"
            :adding="teamsPanel.adding"
            @close="closeTeamsPanel"
            @add="addUserToTeam"
            @remove-team="removeUserFromTeam"
        />

        <logout-prompt-modal
            :is-open="logoutPrompt.show"
            :processing="logoutPrompt.processing"
            @close="closeLogoutPrompt"
            @confirm="confirmLogout"
        />
    </main>
</template>

<script setup>
import { ref, reactive, computed, watch, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import AdminHeaderTabs from '@/screens/_components/admin/HeaderTabs.vue'
import UserActionMenu from '@/screens/_components/admin/UserActionMenu.vue'
import RemoveUserModal from '@/screens/trusted/admin/modals/RemoveUserModal.vue'
import ResendWelcomeEmailModal from '@/screens/trusted/admin/modals/ResendWelcomeEmailModal.vue'
import ResetPasswordModal from '@/screens/trusted/admin/modals/ResetPasswordModal.vue'
import InviteUserSlideIn from '@/screens/trusted/admin/modals/InviteUserSlideIn.vue'
import UpdateUserSlideIn from '@/screens/trusted/admin/modals/UpdateUserSlideIn.vue'
import ManageUserGrantsSlideIn from '@/screens/trusted/admin/modals/ManageUserGrantsSlideIn.vue'
import AddUserGrantSlideIn from '@/screens/trusted/admin/modals/AddUserGrantSlideIn.vue'
import RevokeUserGrantModal from '@/screens/trusted/admin/modals/RevokeUserGrantModal.vue'
import ManageUserTeamsSlideIn from '@/screens/trusted/admin/modals/ManageUserTeamsSlideIn.vue'
import LogoutPromptModal from '@/screens/trusted/admin/modals/LogoutPromptModal.vue'
import { useSessionStore } from '@/stores/session'
import { useAuthStore } from '@/stores/auth'
import UserService from '@/services/user.service'
import TeamService from '@/services/team.service'
import { errorMessage } from '@/utils/notifications'
import { removeBy, upsertBy } from '@/utils/list-mutations'
import { usePermissions, PERMISSIONS } from '@/composables/usePermissions'

// Store
const sessionStore = useSessionStore()
const authStore = useAuthStore()
const router = useRouter()
const { can, isAdmin } = usePermissions()

// State
const loading = ref(false)
const results = ref([])
const userGrants = ref({})
const userTeamMemberships = ref({})

const modals = reactive({
    create: {
        error: null,
        show: false,
        processing: false,
        data: {
            first_name: null,
            last_name: null,
            email: null,
            allow_marketing_emails: false,
        },
    },
    remove: {
        show: false,
        ulid: null,
        processing: false,
    },
    resend: {
        show: false,
        ulid: null,
        processing: false,
    },
    reset: {
        show: false,
        ulid: null,
        processing: false,
    },
    update: {
        error: null,
        show: false,
        processing: false,
        data: {
            ulid: null,
            first_name: null,
            last_name: null,
            email: null,
            allow_marketing_emails: null,
            personal_access_tokens_enabled: null,
            date_format: null,
            time_format: null,
        },
    },
})

// Grants panel state
const grantsPanel = reactive({
    show: false,
    user: null,
    grants: [],
})

const grantModal = reactive({
    show: false,
    processing: false,
    error: null,
    data: {
        role: '',
        scope_type: '',
        selectedProjectUlid: '',
        selectedEnvironmentUlid: '',
    },
})

const revokeModal = reactive({
    show: false,
    processing: false,
    grant: null,
})

// Teams panel state
const teamsPanel = reactive({
    show: false,
    user: null,
    teams: [],
    selectedTeamUlid: '',
    adding: false,
})

// Logout prompt state
const logoutPrompt = reactive({
    show: false,
    processing: false,
})

// Computed
const currentUserUlid = computed(() => sessionStore.data?.user?.data?.ulid)

const projects = computed(() => sessionStore.data?.projects?.data ?? [])

const selectedProjectEnvironments = computed(() => {
    if (!grantModal.data.selectedProjectUlid) return []
    const project = projects.value.find(
        (p) => p.ulid === grantModal.data.selectedProjectUlid
    )
    return project?.environments?.data ?? []
})

const grantRoleDescription = computed(() => {
    switch (grantModal.data.role) {
        case 'admin':
            return 'Full access to all features and settings, including user management and billing.'
        case 'editor':
            return 'Can create, update, and delete projects, flags, targets, segments, and webhooks.'
        case 'viewer':
            return 'Read-only access to projects, flags, targets, and segments.'
        default:
            return ''
    }
})

const canSubmitCreate = computed(
    () =>
        !!modals.create.data.first_name?.trim() &&
        !!modals.create.data.last_name?.trim() &&
        !!modals.create.data.email?.trim()
)

// date_format/time_format default to null and are cleared back to null by
// resetUpdateModal, but Codacy's type-aware linting narrows their type from
// the bound <select> option values (all non-empty strings), so it sees the
// null case as unreachable even though it is not (ContextRule.vue, ZEN-1012,
// carries the same class of false positive).
const canSubmitUpdate = computed(
    () =>
        !!modals.update.data.first_name?.trim() &&
        !!modals.update.data.last_name?.trim() &&
        !!modals.update.data.email?.trim() &&
        // eslint-disable-next-line
        !!modals.update.data.date_format &&
        !!modals.update.data.time_format
)

const canSubmitGrant = computed(() => {
    if (!grantModal.data.role || !grantModal.data.scope_type) return false
    if (
        grantModal.data.role === 'admin' &&
        grantModal.data.scope_type === 'environment'
    )
        return false
    if (
        grantModal.data.scope_type === 'project' &&
        !grantModal.data.selectedProjectUlid
    )
        return false
    if (grantModal.data.scope_type === 'environment') {
        if (
            !grantModal.data.selectedProjectUlid ||
            !grantModal.data.selectedEnvironmentUlid
        )
            return false
    }
    return true
})

async function searchAvailableTeams(query) {
    try {
        const response = await TeamService.search({
            search: query,
            per_page: 20,
        })
        const memberTeamUlids = new Set(teamsPanel.teams.map((g) => g.ulid))
        return (response.data ?? [])
            .filter((team) => !memberTeamUlids.has(team.ulid))
            .map((team) => ({ ulid: team.ulid, label: team.name }))
    } catch (error) {
        errorMessage(error)
        return []
    }
}

// Methods
const refresh = async () => {
    loading.value = true
    try {
        const response = await UserService.list()
        const users = response.data

        // Fetch grants and teams (one request each for all users), into fresh
        // maps so entries for users no longer in `results` don't linger
        // across refreshes
        const newUserGrants = {}
        for (const user of users) {
            newUserGrants[user.ulid] = []
        }
        const grantsPromise = UserService.listAllGrants()
            .then((grantsRes) => {
                for (const grant of grantsRes.data ?? []) {
                    if (newUserGrants[grant.principal_ulid] === undefined) {
                        continue
                    }
                    newUserGrants[grant.principal_ulid].push(grant)
                }
            })
            .catch(() => {
                // Leave newUserGrants empty per-user on failure
            })

        const newUserTeamMemberships = {}
        for (const user of users) {
            newUserTeamMemberships[user.ulid] = []
        }
        const teamsPromise = UserService.listAllTeams()
            .then((teamsRes) => {
                for (const team of teamsRes.data ?? []) {
                    for (const member of team.users?.data ?? []) {
                        if (newUserTeamMemberships[member.ulid] === undefined) {
                            continue
                        }
                        newUserTeamMemberships[member.ulid].push({
                            ulid: team.ulid,
                            name: team.name,
                        })
                    }
                }
            })
            .catch(() => {
                // Leave newUserTeamMemberships empty per-user on failure
            })

        await Promise.all([grantsPromise, teamsPromise])

        // Update all reactive state together so the table never renders a
        // user list without its matching grants/teams data.
        results.value = users
        userGrants.value = newUserGrants
        userTeamMemberships.value = newUserTeamMemberships
    } catch (error) {
        errorMessage(error)
    } finally {
        loading.value = false
    }
}

const resetCreateModal = () => {
    modals.create.data.first_name = null
    modals.create.data.last_name = null
    modals.create.data.email = null
    modals.create.data.allow_marketing_emails = false
    modals.create.error = null
    modals.create.processing = false
}

const resetUpdateModal = () => {
    modals.update.data.ulid = null
    modals.update.data.first_name = null
    modals.update.data.last_name = null
    modals.update.data.email = null
    modals.update.data.allow_marketing_emails = null
    modals.update.data.personal_access_tokens_enabled = null
    modals.update.data.date_format = null
    modals.update.data.time_format = null
    modals.update.error = null
    modals.update.processing = false
}

const toggleCreateModal = () => {
    modals.create.show = !modals.create.show
    if (!modals.create.show) {
        resetCreateModal()
    }
}

const toggleRemoveModal = () => {
    modals.remove.show = !modals.remove.show
    if (!modals.remove.show) {
        modals.remove.ulid = null
        modals.remove.processing = false
    }
}

const toggleResendModal = () => {
    modals.resend.show = !modals.resend.show
    if (!modals.resend.show) {
        modals.resend.ulid = null
        modals.resend.processing = false
    }
}

const toggleResetModal = () => {
    modals.reset.show = !modals.reset.show
    if (!modals.reset.show) {
        modals.reset.ulid = null
        modals.reset.processing = false
    }
}

const toggleUpdateModal = () => {
    modals.update.show = !modals.update.show
    if (!modals.update.show) {
        resetUpdateModal()
    }
}

const confirmCreate = async () => {
    modals.create.processing = true
    try {
        // The response carries the new user, plus the default grant the API
        // created with it in meta.grants. A new user has no teams yet.
        const response = await UserService.create({ ...modals.create.data })
        const user = response.data
        results.value = upsertBy(results.value, user)
        userGrants.value[user.ulid] = response.meta?.grants ?? []
        userTeamMemberships.value[user.ulid] = []
        toggleCreateModal()
    } catch (error) {
        modals.create.error = error
    } finally {
        modals.create.processing = false
    }
}

const confirmRemove = async () => {
    modals.remove.processing = true
    try {
        const ulid = modals.remove.ulid
        await UserService.remove(ulid)
        results.value = removeBy(results.value, ulid)
        delete userGrants.value[ulid]
        delete userTeamMemberships.value[ulid]
        toggleRemoveModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.remove.processing = false
    }
}

const confirmResend = async () => {
    modals.resend.processing = true
    try {
        await UserService.resend(modals.resend.ulid)
        toggleResendModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.resend.processing = false
    }
}

const confirmReset = async () => {
    modals.reset.processing = true
    try {
        await UserService.reset(modals.reset.ulid)
        toggleResetModal()
    } catch (error) {
        errorMessage(error)
    } finally {
        modals.reset.processing = false
    }
}

const confirmUpdate = async () => {
    modals.update.processing = true
    try {
        const response = await UserService.update(modals.update.data.ulid, {
            ...modals.update.data,
        })
        // Replace the row where it sits, so the table keeps its order.
        results.value = upsertBy(results.value, response.data)
        toggleUpdateModal()
    } catch (error) {
        modals.update.error = error
    } finally {
        modals.update.processing = false
    }
}

const openCreateModal = () => {
    toggleCreateModal()
}

const openRemoveModal = (ulid) => {
    modals.remove.ulid = ulid
    toggleRemoveModal()
}

const openResendModal = (ulid) => {
    modals.resend.ulid = ulid
    toggleResendModal()
}

const openResetModal = (ulid) => {
    modals.reset.ulid = ulid
    toggleResetModal()
}

const openUpdateModal = (user) => {
    modals.update.data.ulid = user.ulid
    modals.update.data.first_name = user.first_name
    modals.update.data.last_name = user.last_name
    modals.update.data.email = user.email
    modals.update.data.allow_marketing_emails = user.allow_marketing_emails
    modals.update.data.personal_access_tokens_enabled =
        user.personal_access_tokens_enabled
    modals.update.data.date_format = user.date_format
    modals.update.data.time_format = user.time_format
    toggleUpdateModal()
}

// Grants/Teams helpers
function fullName(user) {
    return (user?.first_name ?? '') + ' ' + (user?.last_name ?? '')
}

function scopeLabel(grant) {
    if (!grant) return ''
    if (grant.scope_type === 'account') return 'Account'
    if (grant.scope_type === 'project') {
        const project = projects.value.find((p) => p.ulid === grant.scope_ulid)
        return project ? project.name : 'Project'
    }
    if (grant.scope_type === 'environment') {
        for (const project of projects.value) {
            const env = project.environments?.data?.find(
                (e) => e.ulid === grant.scope_ulid
            )
            if (env) return `${project.name} / ${env.name}`
        }
        return 'Environment'
    }
    return grant.scope_type
}

// Grants panel methods
async function openGrantsPanel(user) {
    grantsPanel.user = user
    grantsPanel.grants = []
    grantsPanel.show = true
    await fetchUserGrants(user.ulid)
}

function closeGrantsPanel() {
    grantsPanel.show = false
    grantsPanel.user = null
    grantsPanel.grants = []
}

async function fetchUserGrants(userUlid) {
    try {
        const response = await UserService.listGrants(userUlid)
        grantsPanel.grants = response.data ?? []
        userGrants.value[userUlid] = grantsPanel.grants
    } catch (err) {
        errorMessage(err)
    }
}

// Apply one change to the panel's grants and to the user's row in the table,
// so both lists stay in step without a re-fetch.
function changeUserGrants(userUlid, change) {
    grantsPanel.grants = change(grantsPanel.grants)
    userGrants.value[userUlid] = change(userGrants.value[userUlid] ?? [])
}

function openAddGrantModal() {
    grantModal.show = true
    grantModal.error = null
    grantModal.processing = false
    grantModal.data.role = ''
    grantModal.data.scope_type = ''
    grantModal.data.selectedProjectUlid = ''
    grantModal.data.selectedEnvironmentUlid = ''
}

function closeAddGrantModal() {
    grantModal.show = false
}

async function submitGrant() {
    if (!grantsPanel.user) return

    grantModal.processing = true
    grantModal.error = null

    const accountUlid = sessionStore.data?.account?.data?.ulid
    let scopeUlid = accountUlid

    if (grantModal.data.scope_type === 'project') {
        scopeUlid = grantModal.data.selectedProjectUlid
    } else if (grantModal.data.scope_type === 'environment') {
        scopeUlid = grantModal.data.selectedEnvironmentUlid
    }

    const userUlid = grantsPanel.user.ulid

    try {
        const response = await UserService.createGrant(userUlid, {
            role: grantModal.data.role,
            scope_type: grantModal.data.scope_type,
            scope_ulid: scopeUlid,
        })
        changeUserGrants(userUlid, (grants) =>
            upsertBy(grants, response.data, 'ulid')
        )
        closeAddGrantModal()
        promptLogoutIfSelf(userUlid)
    } catch (err) {
        grantModal.error = err
    } finally {
        grantModal.processing = false
    }
}

function revokeGrantConfirm(grant) {
    revokeModal.grant = grant
    revokeModal.show = true
    revokeModal.processing = false
}

function closeRevokeModal() {
    revokeModal.show = false
    revokeModal.grant = null
}

async function confirmRevoke() {
    if (!grantsPanel.user) return
    revokeModal.processing = true
    const userUlid = grantsPanel.user.ulid
    const grantUlid = revokeModal.grant.ulid
    try {
        await UserService.revokeGrant(userUlid, grantUlid)
        changeUserGrants(userUlid, (grants) => removeBy(grants, grantUlid))
        closeRevokeModal()
        promptLogoutIfSelf(userUlid)
    } catch (err) {
        errorMessage(err)
    } finally {
        revokeModal.processing = false
    }
}

// Teams panel methods
async function openTeamsPanel(user) {
    teamsPanel.user = user
    teamsPanel.teams = []
    teamsPanel.selectedTeamUlid = ''
    teamsPanel.show = true
    await fetchUserTeamsList(user.ulid)
}

function closeTeamsPanel() {
    teamsPanel.show = false
    teamsPanel.user = null
    teamsPanel.teams = []
    teamsPanel.selectedTeamUlid = ''
}

async function fetchUserTeamsList(userUlid) {
    try {
        const response = await UserService.listTeams(userUlid)
        teamsPanel.teams = response.data ?? []
        userTeamMemberships.value[userUlid] = teamsPanel.teams
    } catch (err) {
        errorMessage(err)
    }
}

// Apply one change to the panel's teams and to the user's row in the table,
// so both lists stay in step without a re-fetch.
function changeUserTeams(userUlid, change) {
    teamsPanel.teams = change(teamsPanel.teams)
    userTeamMemberships.value[userUlid] = change(
        userTeamMemberships.value[userUlid] ?? []
    )
}

async function addUserToTeam() {
    if (!teamsPanel.selectedTeamUlid || !teamsPanel.user) return
    teamsPanel.adding = true
    const userUlid = teamsPanel.user.ulid
    try {
        const response = await UserService.addTeam(
            userUlid,
            teamsPanel.selectedTeamUlid
        )
        teamsPanel.selectedTeamUlid = ''
        changeUserTeams(userUlid, (teams) =>
            upsertBy(teams, response.data, 'ulid')
        )
        promptLogoutIfSelf(userUlid)
    } catch (err) {
        errorMessage(err)
    } finally {
        teamsPanel.adding = false
    }
}

async function removeUserFromTeam(teamUlid) {
    if (!teamsPanel.user) return
    const userUlid = teamsPanel.user.ulid
    try {
        await UserService.removeTeam(userUlid, teamUlid)
        changeUserTeams(userUlid, (teams) => removeBy(teams, teamUlid, 'ulid'))
        promptLogoutIfSelf(userUlid)
    } catch (err) {
        errorMessage(err)
    }
}

// Self-edit logout prompt
function promptLogoutIfSelf(userUlid) {
    if (userUlid === currentUserUlid.value) {
        logoutPrompt.show = true
    }
}

function closeLogoutPrompt() {
    logoutPrompt.show = false
    logoutPrompt.processing = false
}

async function confirmLogout() {
    logoutPrompt.processing = true
    try {
        await authStore.logout(true)
        await router.push('/login')
    } catch {
        logoutPrompt.processing = false
    }
}

// Grant form cascade watchers
watch(
    () => grantModal.data.role,
    (newRole) => {
        if (
            newRole === 'admin' &&
            grantModal.data.scope_type === 'environment'
        ) {
            grantModal.data.scope_type = 'account'
        }
    }
)

watch(
    () => grantModal.data.scope_type,
    () => {
        grantModal.data.selectedProjectUlid = ''
        grantModal.data.selectedEnvironmentUlid = ''
    }
)

watch(
    () => grantModal.data.selectedProjectUlid,
    () => {
        grantModal.data.selectedEnvironmentUlid = ''
    }
)

// Lifecycle
onMounted(() => {
    refresh()
})
</script>
